<template>
    <div
        class="modal victory"
        :class="{['minimized']: minimized}"
        v-if="show"
    >
        <div class="modal-title hide-on-minimized">
            <royal-coin :color="winningTeam.color" :theme="gameState.theme" />
            <span class="text">{{ renderVictoryTitle() }}</span>
            <royal-coin :color="winningTeam.color" :theme="gameState.theme" />
        </div>
        <div class="modal-content hide-on-minimized">
            {{ renderVictoryMessage() }}

            <div class="skill-rating-info" v-if="showSkillRatingInfo">
                <rank :skillRating="updatedSkillRating ? updatedSkillRating : prevSkillRating" />

                <span class="skill-rating-value">
                    <span class="skill-rating-score">
                        <count-up
                            :startValue="prevSkillRating"
                            :endValue="nextSkillRating"
                            :callback="countUpCallback"
                        />
                    </span>
                    <span class="skill-rating-label">SR</span>
                </span>
            </div>
        </div>
        <div class="modal-footer">
			<router-link :to="linkToWaitingRoom" class="link" v-if="gameState.queueMode && this.activeWaitingRoom">
                <button class="btn btn-outline">
                    Return to Room
                </button>
            </router-link>
            <router-link :to="redirectTo" class="link">
                <button class="btn btn-primary">
                    Return to Main Site
                </button>
            </router-link>
        </div>
        <span class="modal-toggle" @click="onToggleModal()">
            <fa-icon
                icon="window-minimize"
                class="minimize-modal-icon"
                v-if="!minimized"
            />
            <fa-icon
                icon="chevron-up"
                class="maximize-modal-icon"
                v-if="minimized"
            />
        </span>
    </div>
</template>

<script>
import find from 'lodash.find';
import { formatUsername } from '../../helpers/general';
import api from '../../helpers/api';

export default {
    name: 'VictoryModal',
    props: ['show', 'redirect', 'gameState', 'player'],
	async mounted() {
		if (this.gameState.queueMode) {
			const response = await api.request('/user/waitingGames');

			if (response.ok) {
				try {
					const waitingRooms = await response.json();

					if (waitingRooms.length) {
						this.activeWaitingRoom = waitingRooms[0];
					}
				} catch {
					this.activeWaitingRoom = null;
				}
			}
		} else {
			this.activeWaitingRoom = null;
		}
	},
    data() {
        return {
            minimized: false,
			activeWaitingRoom: null,
        };
    },
    computed: {
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        nextSkillRating() {
            return this.player.nextSkillRating;
        },
        loaded() {
            return this.gameState.loaded;
        },
        players() {
            return this.gameState.players;
        },
        prevSkillRating() {
            return this.player.prevSkillRating;
        },
        ranked() {
            return this.gameState.ranked;
        },
        showSkillRatingInfo() {
            return this.loaded &&
                this.ranked &&
                this.skillRatingHasChanged &&
                !this.redirect.includes('matches') &&
                !this.isSpectator;
        },
        skillRatingHasChanged() {
            return this.player.prevSkillRating !== this.player.nextSkillRating;
        },
        updatedSkillRating() {
            return this.gameState.updatedSkillRating;
        },
        winningPlayers() {
            return this.players.filter(player => player.teamId === this.gameState.winningTeamId);
        },
        winningTeam() {
            return find(this.gameState.teams, { id: this.gameState.winningTeamId });
        },
        redirectTo() {
            return typeof this.redirect === 'string' ?
                this.redirect.replace(/^([^/])/, '/$1') :
                '/';
        },
		linkToWaitingRoom() {
			return this.activeWaitingRoom ?
				`/game/${this.activeWaitingRoom.id}` :
				'/rooms';
		},
		userId() {
            return this.$store.state.loginState.userId;
        },
    },
    methods: {
        countUpCallback(updatedSkillRating) {
            this.$store.dispatch('setUpdatedSkillRating', updatedSkillRating);
        },
        onToggleModal() {
            this.minimized = !this.minimized;
        },
        renderVictoryMessage() {
            const winnerNames = this.winningPlayers.map(player => formatUsername(player.user.username));
            const message = `${winnerNames.join(' and ')} ${winnerNames.length > 1 ? 'have' : 'has'} won the game!`;

            return message;
        },
        renderVictoryTitle() {
            const winningPlayers = this.winningPlayers;
            const isWinner = find(winningPlayers, { id: this.player.id });
            return isWinner ? 'Victory' : 'Defeat';
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .modal {

        &.victory {

            .modal-title {
                display: flex;
                justify-content: space-between;

                .royal-coin {
                    align-self: center;
                    vertical-align: middle;
                    display: inline-block;
                    height: 42px;
                    width: auto;
                }

                .text {
                    vertical-align: middle;
                    align-self: center;
                }
            }

            .modal-content {
                margin-bottom: 20px;
            }

            .modal-footer {
				display: flex;
				justify-content: center;
				gap: 10px;

                .link {
                    text-decoration: none;
                }

                .btn {
                    display: block;
                    margin: 0 auto;
                }
            }

            .skill-rating-info {
                margin-top: 20px;

                .skill-rating-value {
                    position: relative;
                    display: inline-block;
                    font-style: italic;

                    .skill-rating-score {
                        font-size: 1.1em;
                    }

                    .skill-rating-label {
                        font-size: .7em;
                        position: absolute;
                        top: 3px;
                        right: -16px;
                    }
                }
            }

            &.minimized {

                .modal-title {
                    display: none;
                }
            }
        }
    }

</style>
